import React, { useEffect, useState } from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid";
import CloseButton from "../closeButton/CloseButton";

interface SnackBarProps {
  title?: string;
  body?: React.ReactNode;
  variant?: "success" | "error" | "info" | "warning";
  icon?: boolean;
  isVisible?: boolean;
  onClose?: () => void;
  fullWidth?: boolean;
  persist?: boolean;
  style?: React.CSSProperties;
  index?: number;
  hook?: boolean;
}

const VARIANT_CLASSES = {
  warning: {
    bg: "bg-yellow-50",
    border: "border-yellow-400",
    text: "text-yellow-700",
    icon: <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />,
  },
  error: {
    bg: "bg-red-50",
    border: "border-red-400",
    text: "text-red-800",
    icon: <XCircleIcon className="h-5 w-5 text-red-400" />,
  },
  info: {
    bg: "bg-blue-50",
    border: "border-blue-400",
    text: "text-blue-700",
    icon: <InformationCircleIcon className="h-5 w-5 text-blue-400" />,
  },
  success: {
    bg: "bg-green-50",
    border: "border-green-400",
    text: "text-green-800",
    icon: <CheckCircleIcon className="h-5 w-5 text-green-400" />,
  },
};

const SnackBar = ({
  title,
  body,
  variant = "success",
  icon = true,
  isVisible = true,
  onClose,
  fullWidth = true,
  persist = false,
  hook = false,
}: SnackBarProps) => {
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsFadingOut(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !persist) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, persist]);

  const handleClose = () => {
    if (isFadingOut) return;
    setIsFadingOut(true);
    setTimeout(() => {
      onClose?.();
    }, 500);
  };

  if (!isVisible) return null;

  const classes = VARIANT_CLASSES[variant] || VARIANT_CLASSES.success;
  const animationClass = isFadingOut ? "animate-fade-out" : "animate-slide-down";
  const hookClass = hook ? "" : "fixed top-0 left-0 right-0 z-50";

  return (
    <div className={hookClass}>
      <div className="max-w-screen-xl mx-auto px-4">
        <div
          className={`${fullWidth ? "w-full" : "w-fit mx-auto"} border-l-4 ${classes.border} ${
            classes.bg
          } p-4 rounded-md shadow-md ${animationClass} relative`}
        >
          <div className="absolute right-2 top-2">
            <CloseButton onClick={handleClose} colorClass={classes.text} />
          </div>
          <div className="flex items-start">
            {icon && <div className="shrink-0">{classes.icon}</div>}
            <div className={`ml-3 ${icon ? "flex-1" : ""}`}>
              {title && <p className={`font-medium ${classes.text}`}>{title}</p>}
              {body && <div className={`text-sm ${classes.text}`}>{body}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnackBar;
